import React from "react";
import { motion } from "framer-motion";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";
import { ArrowUpRight } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const MENU_NORMAL_RESERVE = "https://pnssm.pro/2ef0e7n";

export const FAQ = () => {
  const { t } = useT();
  const x = t.faq;
  return (
    <section className="relative section-pad section-pad-bottom-tight overflow-hidden bg-noir" data-testid="faq-section">
      <div className="absolute inset-0 grain pointer-events-none opacity-50" />
      <div className="relative max-w-[920px] mx-auto">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.7 }} className="eyebrow"
          >
            {x.eyebrow}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 0.61, 0.36, 1] }}
            className="mt-5 font-display font-light text-[34px] sm:text-[46px] md:text-[60px] leading-[1.05] tracking-[-0.012em] text-[var(--pn-milk)]"
          >
            {x.title_a}
            <br />
            <em className="italic text-[var(--pn-amber)]">{x.title_b}</em>
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.9 }}
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            <AccordionItem
              value="q1"
              className="border-b border-[var(--pn-amber)]/15 first:border-t first:border-[var(--pn-amber)]/15"
            >
              <AccordionTrigger
                className="py-6 md:py-7 text-left font-display font-light text-[19px] md:text-[24px] text-[var(--pn-milk)] hover:text-[var(--pn-amber)] hover:no-underline transition-colors [&>svg]:text-[var(--pn-amber)] [&>svg]:opacity-60 [&[data-state=open]>svg]:opacity-100"
                data-testid="faq-q1"
              >
                {x.q1.q}
              </AccordionTrigger>
              <AccordionContent className="pb-7 pr-2 md:pr-12">
                <p className="text-[15px] md:text-[16.5px] leading-[1.75] text-[var(--pn-cream)]/80 font-light">
                  {x.q1.a1}
                </p>
                <p className="mt-4 text-[14px] text-[var(--pn-cream)]/55 italic font-display leading-relaxed">
                  {x.q1.a2}
                </p>
                <a href={MENU_NORMAL_RESERVE} target="_blank" rel="noopener noreferrer"
                  className="cta-outline-amber mt-7" data-testid="faq-q1-link"
                >
                  <span className="tracking-[0.14em]">{x.q1.cta}</span>
                  <ArrowUpRight size={15} strokeWidth={1.6} />
                </a>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="q2" className="border-b border-[var(--pn-amber)]/15">
              <AccordionTrigger
                className="py-6 md:py-7 text-left font-display font-light text-[19px] md:text-[24px] text-[var(--pn-milk)] hover:text-[var(--pn-amber)] hover:no-underline transition-colors [&>svg]:text-[var(--pn-amber)] [&>svg]:opacity-60 [&[data-state=open]>svg]:opacity-100"
                data-testid="faq-q2"
              >
                {x.q2.q}
              </AccordionTrigger>
              <AccordionContent className="pb-7 pr-2 md:pr-12">
                <p className="font-display italic text-[20px] md:text-[24px] text-[var(--pn-amber)] leading-tight">
                  {x.q2.short}
                </p>
                <p className="mt-4 text-[15px] md:text-[16.5px] leading-[1.75] text-[var(--pn-cream)]/80 font-light">
                  {x.q2.a1}
                </p>
                <p className="mt-3 text-[14px] text-[var(--pn-cream)]/55 italic font-display leading-relaxed">
                  {x.q2.a2}
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="q3" className="border-b border-[var(--pn-amber)]/15">
              <AccordionTrigger
                className="py-6 md:py-7 text-left font-display font-light text-[19px] md:text-[24px] text-[var(--pn-milk)] hover:text-[var(--pn-amber)] hover:no-underline transition-colors [&>svg]:text-[var(--pn-amber)] [&>svg]:opacity-60 [&[data-state=open]>svg]:opacity-100"
                data-testid="faq-q3"
              >
                {x.q3.q}
              </AccordionTrigger>
              <AccordionContent className="pb-7 pr-2 md:pr-12">
                <p className="text-[15px] md:text-[16.5px] leading-[1.75] text-[var(--pn-cream)]/80 font-light">
                  {x.q3.a1}
                </p>
                <p className="mt-4 text-[15px] md:text-[16.5px] leading-[1.75] text-[var(--pn-cream)]/80 font-light">
                  {x.q3.a2_pre}
                  <em className="italic text-[var(--pn-amber)] font-display">{x.q3.a2_em}</em>
                  {x.q3.a2_post}
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
